import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { fmt, Variance, StatusBadge, Summary } from "./reconUtils";

function Line({ l, pending }) {
  return (
    <div className={`p-2 text-sm ${l.would_go_negative ? "bg-red-50" : ""}`} data-testid={`recon-detail-line-${l.sku}`}>
      <div className="flex justify-between gap-2">
        <div className="min-w-0"><div className="truncate">{l.name}</div><div className="font-mono text-[11px] text-muted-foreground">{l.sku} · {l.unit}</div></div>
        <Variance value={l.applied_delta ?? l.variance} testid={`recon-detail-variance-${l.sku}`} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-1 font-mono text-[11px] text-muted-foreground">
        <span>System at count: <b className="text-foreground">{fmt(l.system_qty)}</b></span>
        <span>Physical: <b className="text-foreground">{fmt(l.counted_qty)}</b></span>
        {pending ? <>
          <span>Now in system: <b className="text-foreground">{fmt(l.current_qty)}</b></span>
          <span>After approval: <b className={l.would_go_negative ? "text-[#C05746]" : "text-foreground"} data-testid={`recon-projected-${l.sku}`}>{fmt(l.projected_qty)}</b></span>
        </> : l.final_qty !== undefined && <span>Final after approval: <b className="text-foreground">{fmt(l.final_qty)}</b></span>}
      </div>
      {pending && l.movement_since_count !== 0 && <div className="text-[11px] text-amber-700 mt-1">Sales/receipts since count: {l.movement_since_count > 0 ? "+" : ""}{fmt(l.movement_since_count)} — kept; only the counted variance is applied.</div>}
    </div>
  );
}

export default function ReconDetailDialog({ recId, canDecide, onClose, onDone }) {
  const [rec, setRec] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRec(null); setNotes("");
    if (recId) api.get(`/inventory/reconciliations/${recId}`).then((r) => setRec(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Could not load"));
  }, [recId]);
  const pending = rec?.status === "pending";
  const lines = pending ? rec.impact : rec?.variances || [];
  const negative = lines.some((l) => l.would_go_negative);
  const decide = async (action) => {
    if (action === "reject" && !notes.trim()) return toast.error("Give a reason for rejecting");
    setBusy(true);
    try {
      await api.post(`/inventory/reconcile/${recId}/${action}`, { notes });
      toast.success(action === "approve" ? "Approved — stock adjusted" : "Rejected — stock unchanged");
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!recId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recon-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">Reconciliation {rec && <StatusBadge status={rec.status} testid="recon-detail-status" />}</DialogTitle>
          <DialogDescription>
            {rec ? <>Counted by {rec.counted_by_name || rec.actor_name} · {new Date(rec.submitted_at || rec.at).toLocaleString("en-ZA")}
              {rec.approved_by_name && <> · {rec.status === "rejected" ? "Rejected" : "Approved"} by {rec.approved_by_name} · {new Date(rec.approved_at).toLocaleString("en-ZA")}</>}</> : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        {rec && <>
          <Summary lines={lines} />
          {rec.notes && <div className="text-xs text-muted-foreground" data-testid="recon-detail-notes">{rec.notes}</div>}
          {negative && <div className="text-sm text-[#C05746]" data-testid="recon-negative-warning">Some lines would make stock negative because of sales since the count. Reject and recount those products.</div>}
          <div className="border rounded-md divide-y">{lines.map((l) => <Line key={l.product_id} l={l} pending={pending} />)}</div>
          {pending && canDecide && <Textarea placeholder="Comment / reason (required to reject)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="recon-decision-notes" />}
        </>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {pending && canDecide && <>
            <Button variant="outline" className="text-[#C05746]" disabled={busy} onClick={() => decide("reject")} data-testid="recon-reject-btn"><X className="w-4 h-4 mr-1" />Reject</Button>
            <Button className="rounded-full" disabled={busy || negative} onClick={() => decide("approve")} data-testid="recon-approve-btn"><Check className="w-4 h-4 mr-1" />Approve & adjust stock</Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
